import BENCHMARKS_API_V1 from 'api/benchmarks/v1'
import i18n from 'i18next'
import { all, call, debounce, put, select, takeLatest } from 'redux-saga/effects'
import useLocationFilters from 'utils/hooks/useLocationFilters'
import prepareFilterParams from 'utils/prepareFilterParams'
import { notificationShow } from 'modules/Notifications/actions'
import {
  FETCH,
  QUERY_TYPE,
  REMOVE_FROM_LIST,
  SET_ARCHIVE_PROCESSING_IDS,
  SET_BENCHMARKS,
  SET_FILTER,
  SET_FILTERS,
  SETUP_FILTERS,
  TOGGLE_ARCHIVE,
  UPDATE,
} from 'modules/Recommendations/Benchmarks/types'
import { FETCH_NOTIFICATIONS } from 'modules/Recommendations/Audits/types'

function* getList({ payload }) {
  const { pagination } = yield select((state) => state['recommendations/benchmarks'])
  try {
    yield put({
      type: UPDATE,
      payload: { loading: true },
    })

    const { response, error } = yield call(BENCHMARKS_API_V1.fetchAll, {
      params: payload.params,
    })

    if (error) {
      throw error
    }

    const {
      data,
      pagination: { total },
    } = response

    yield put({
      type: SET_BENCHMARKS,
      payload: {
        data,
        pagination: {
          ...pagination,
          total,
        },
      },
    })
  } catch (error) {
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_fetch_failed',
      }),
    )
    console.logXHR(error)
  } finally {
    yield put({
      type: UPDATE,
      payload: { loading: false },
    })
    yield put({ type: FETCH_NOTIFICATIONS })
  }
}

function* notify(payload) {
  yield put(payload)
}

function* toggleArchiveBenchmarks({ payload, meta: { resolve = () => {} } = {} }) {
  const { action } = payload
  const { filters, data } = yield select((state) => state['recommendations/benchmarks'])

  yield put({
    type: SET_ARCHIVE_PROCESSING_IDS,
    payload: payload.ids,
  })

  try {
    const { response, error } = yield call(BENCHMARKS_API_V1[action], {
      data: payload,
    })

    if (error) {
      throw error
    }

    const listWithNames = response.data.map((item) => ({
      id: item.id,
      name: data.find((row) => row.id === item.id).name,
      status: item.status,
    }))

    const successArchived = listWithNames.filter((item) => item.id && item.status === 'success')
    const failureArchived = listWithNames.filter((item) => item.id && item.status === 'failed')

    if (successArchived.length > 0) {
      yield all(
        successArchived.map(({ name }) =>
          call(
            notify,
            notificationShow({
              type: 'success',
              message: i18n.t(`benchmarks::notifications.success.${action}`, {
                name,
              }),
            }),
          ),
        ),
      )
    }

    if (failureArchived.length > 0) {
      yield all(
        failureArchived.map(({ name }) =>
          call(
            notify,
            notificationShow({
              type: 'error',
              message: i18n.t(`benchmarks::notifications.failed.${action}`, {
                name,
              }),
            }),
          ),
        ),
      )
    }

    if (successArchived.length > 0) {
      yield put({
        type: REMOVE_FROM_LIST,
        payload: successArchived.map(({ id }) => id),
      })

      yield put({
        type: FETCH,
        payload: {
          params: {
            ...prepareFilterParams(filters),
            offset: data.length - successArchived.length,
            limit: successArchived.length,
          },
        },
      })
    }
  } catch (error) {
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_fetch_failed',
      }),
    )
    console.logXHR(error)
  } finally {
    yield put({
      type: SET_ARCHIVE_PROCESSING_IDS,
      payload: [],
    })
    yield put({
      type: UPDATE,
      payload: { loading: false },
    })
    resolve()
  }
}

function* typeQuery({ payload }) {
  yield put({ type: SET_FILTER, payload })
}

function* setupFilters() {
  const filtersPayload = useLocationFilters(global.location)

  yield put({
    type: SET_FILTERS,
    payload: filtersPayload,
  })
}

export default function* Sagas() {
  yield takeLatest(FETCH, getList)
  yield takeLatest(TOGGLE_ARCHIVE, toggleArchiveBenchmarks)
  yield takeLatest(SETUP_FILTERS, setupFilters)
  yield debounce(500, QUERY_TYPE, typeQuery)
}
